import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'
import BigButton from '../components/BigButton'
import PaymentBlock from '../components/PaymentBlock'
import type { Payment } from '../lib/types'

type State = {
  naam?: string
  email?: string
  emailSent?: boolean
  buurtbewoner?: boolean
  assigned?: string[]
  payment?: Payment
}

function firstName(naam: string | undefined): string {
  return (naam ?? '').trim().split(/\s+/)[0] || 'rommelmarkt-vriend'
}

export default function Bedankt() {
  const { state } = useLocation() as { state: State | null }

  // No router state means the user landed here directly (bookmark, back-
  // button after refresh, shared link). Don't pretend the registration
  // succeeded — send them to the form with a short note.
  if (!state?.naam) {
    return (
      <div className="container-narrow py-12">
        <div className="card p-8 shadow-block text-center">
          <h1 className="font-display text-5xl tracking-display uppercase mb-3">
            Geen inschrijving gevonden
          </h1>
          <p className="text-base text-muted mb-6">
            Je bent hier rechtstreeks terechtgekomen. Schrijf je eerst in via de knop hieronder.
          </p>
          <Link to="/inschrijven">
            <BigButton>Naar het inschrijfformulier</BigButton>
          </Link>
        </div>
      </div>
    )
  }

  const voornaam = firstName(state.naam)
  const email = state.email
  const emailSent = state.emailSent ?? false
  const buurtbewoner = state.buurtbewoner ?? false
  const assigned = state.assigned ?? []
  const payment = state.payment

  let body: JSX.Element
  if (buurtbewoner && assigned.length > 0) {
    body = (
      <>
        <p className="text-lg mb-3">
          Je plek is gereserveerd: <strong>{assigned.join(', ')}</strong>.
        </p>
        <p className="text-base text-muted mb-3">
          Definitief bevestigd zodra de betaling binnen is. Een bevestigingsmail met de betaalgegevens
          is onderweg.
        </p>
      </>
    )
  } else if (buurtbewoner) {
    body = (
      <>
        <p className="text-lg mb-3">We hebben je inschrijving ontvangen.</p>
        <p className="text-base text-muted mb-3">
          Het team zoekt jouw plek manueel — je hoort hier deze week iets over.
        </p>
      </>
    )
  } else {
    body = (
      <>
        <p className="text-lg mb-3">We hebben je inschrijving ontvangen.</p>
        <p className="text-base text-muted mb-3">
          Buurtbewoners krijgen tot 31 augustus voorrang. Op <strong>1 september</strong> sturen we
          jou een mail met je toegewezen zone en de link om er zelf je plek te kiezen.
        </p>
      </>
    )
  }

  return (
    <div className="container-narrow py-12 space-y-6">
      <div className="card p-8 shadow-block text-center">
        <img src="/img/logo-rond.png" alt="" className="mx-auto h-24 w-24 mb-3" />
        <h1 className="font-display text-6xl tracking-display uppercase mb-2">Bedankt, {voornaam}!</h1>
        {body}
        {!emailSent && email && (
          <p className="text-sm text-muted mb-3">
            We konden je geen bevestigingsmail sturen — bewaar deze pagina of mail
            <a className="underline" href="mailto:rommelmarkt@fluweelfeest.be"> rommelmarkt@fluweelfeest.be</a>.
          </p>
        )}
        <p className="text-base text-muted">Geen mail ontvangen? Kijk even in je spam-folder.</p>
      </div>

      {payment && <PaymentBlock payment={payment} title="Betaal je plek" variant="ink" />}

      <div className="border-[3px] border-ink bg-offwhite p-5 text-center">
        <p className="font-display text-2xl tracking-display uppercase mb-1">Praktisch op de dag zelf</p>
        <p className="text-base text-muted mb-3">
          De uren, hoe groot je plek is, waar je je auto kwijt kan en wat je mag verkopen.
        </p>
        <Link to="/praktisch">
          <BigButton>Lees de praktische info</BigButton>
        </Link>
      </div>

      <div className="text-center">
        <Link to="/">
          <BigButton variant="ghost">Terug naar de start</BigButton>
        </Link>
      </div>
    </div>
  )
}
